import { useEffect, useRef, useState, useCallback } from 'react';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogContent,
    Stack,
    Typography,
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import server from '../../../global/server';
import { useSnackbar } from 'notistack';
import useHandleErrors from '../../../hooks/useHandleErrors';

const COLORS = ['#006a61', '#1E7B54', '#2e7d32', '#0288d1', '#7b1fa2', '#c62828', '#ef6c00', '#00838f', '#4527a0', '#558b2f'];

const RaffleWheel = ({ open, onClose, participants, scheduleData, mutate }) => {
    const canvasRef = useRef(null);
    const [spinning, setSpinning] = useState(false);
    const [winner, setWinner] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [errs, setErrs] = useState(null);
    useHandleErrors(errs);
    const { enqueueSnackbar } = useSnackbar();
    const animRef = useRef(null);

    const count = participants?.length || 0;
    const sliceAngle = count > 0 ? (2 * Math.PI) / count : 0;

    const drawWheel = useCallback((rot) => {
        const canvas = canvasRef.current;
        if (!canvas || count === 0) return;
        const ctx = canvas.getContext('2d');
        const size = canvas.width;
        const center = size / 2;
        const radius = center - 10;

        ctx.clearRect(0, 0, size, size);

        participants.forEach((p, i) => {
            const startAngle = rot + i * sliceAngle;
            const endAngle = startAngle + sliceAngle;

            // Fatia
            ctx.beginPath();
            ctx.moveTo(center, center);
            ctx.arc(center, center, radius, startAngle, endAngle);
            ctx.closePath();
            ctx.fillStyle = COLORS[i % COLORS.length];
            ctx.fill();
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Texto
            ctx.save();
            ctx.translate(center, center);
            ctx.rotate(startAngle + sliceAngle / 2);
            ctx.textAlign = 'right';
            ctx.fillStyle = 'white';
            ctx.font = `bold ${Math.min(14, Math.max(10, 140 / count))}px Roboto, sans-serif`;
            const name = p.name.length > 12 ? p.name.substring(0, 11) + '...' : p.name;
            ctx.fillText(name, radius - 15, 5);
            ctx.restore();
        });

        // Centro
        ctx.beginPath();
        ctx.arc(center, center, 20, 0, 2 * Math.PI);
        ctx.fillStyle = 'white';
        ctx.fill();
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 2;
        ctx.stroke();
    }, [participants, count, sliceAngle]);

    useEffect(() => {
        if (open && canvasRef.current) {
            drawWheel(rotation);
        }
    }, [open, rotation, drawWheel]);

    const spin = () => {
        if (spinning || count === 0) return;
        setSpinning(true);
        setWinner(null);

        // Escolher vencedor aleatoriamente no frontend
        const winnerIndex = Math.floor(Math.random() * count);

        // Calcular rotação final: múltiplas voltas + posicionar o vencedor no topo (ponteiro)
        // O ponteiro está no topo (ângulo 3π/2 = 270°)
        // O centro da fatia vencedora deve parar ali
        const targetAngle = -(winnerIndex * sliceAngle + sliceAngle / 2) - Math.PI / 2;
        const fullSpins = 5 + Math.floor(Math.random() * 3); // 5-7 voltas completas
        const finalRotation = targetAngle + fullSpins * 2 * Math.PI;

        const startRotation = rotation;
        const totalRotation = finalRotation - startRotation;
        const duration = 4000 + Math.random() * 1000; // 4-5 segundos
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing: desaceleração cúbica
            const eased = 1 - Math.pow(1 - progress, 3);
            const currentRot = startRotation + totalRotation * eased;

            setRotation(currentRot);
            drawWheel(currentRot);

            if (progress < 1) {
                animRef.current = requestAnimationFrame(animate);
            } else {
                setRotation(finalRotation);
                setSpinning(false);
                setWinner(participants[winnerIndex]);

                // Confirmar no backend
                confirmRaffle(participants[winnerIndex].order_id);
            }
        };

        animRef.current = requestAnimationFrame(animate);
    };

    const confirmRaffle = async (winnerOrderId) => {
        try {
            await server.patch('/profile/schedule/raffle', {
                meeting_id: scheduleData.meeting_id,
                datetime: scheduleData.datetime,
                meeting_scheduled_id: scheduleData.meeting_scheduled_id,
                winner_order_id: winnerOrderId,
            });
            enqueueSnackbar('Sorteio realizado com sucesso!', { variant: 'success' });
            mutate?.();
        } catch (error) {
            setErrs(error);
        }
    };

    useEffect(() => {
        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, []);

    const handleClose = () => {
        if (spinning) return;
        setWinner(null);
        onClose();
    };

    if (!participants || count === 0) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ bgcolor: '#fafafa', p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                        Roleta do Sorteio
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {new Date(scheduleData.datetime).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                        {' · '}{count} participante{count > 1 ? 's' : ''}
                    </Typography>
                </Box>

                {/* Roleta */}
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', py: 3 }}>
                    {/* Ponteiro */}
                    <Box sx={{
                        position: 'absolute',
                        top: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        width: 0,
                        height: 0,
                        borderLeft: '12px solid transparent',
                        borderRight: '12px solid transparent',
                        borderTop: '24px solid #c62828',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }} />

                    <canvas
                        ref={canvasRef}
                        width={300}
                        height={300}
                        style={{ borderRadius: '50%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
                    />
                </Box>

                {/* Resultado */}
                {winner && (
                    <Box sx={{
                        bgcolor: '#e8f5e9',
                        p: 3,
                        textAlign: 'center',
                        animation: 'fadeIn 0.5s ease',
                        '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(10px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
                    }}>
                        <CelebrationIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                        <Typography variant="h6" fontWeight={700} color="#2e7d32">
                            Parabéns!
                        </Typography>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mt: 1.5 }}>
                            <Avatar src={winner.profileImage} sx={{ width: 44, height: 44, border: '3px solid #2e7d32' }}>
                                {winner.name?.[0]}
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {winner.name}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            foi sorteado(a) para participar do encontro!
                        </Typography>
                    </Box>
                )}

                {/* Botão */}
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    {!winner ? (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={spin}
                            disabled={spinning}
                            sx={{
                                bgcolor: '#006a61',
                                borderRadius: '8px',
                                height: 48,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                '&:hover': { bgcolor: '#005a52' },
                            }}
                        >
                            {spinning ? 'Girando...' : 'Girar roleta'}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleClose}
                            sx={{
                                borderRadius: '8px',
                                height: 48,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: '#006a61',
                                color: '#006a61',
                            }}
                        >
                            Fechar
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default RaffleWheel;
